import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import Card from "@mui/material/Card";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import BasicLayout from "layouts/authentication/components/BasicLayout";

import { GoogleLogin } from "@react-oauth/google";

import bgImage from "assets/images/bg-sign-in-basic.jpeg";

/*
|--------------------------------------------------------------------------
| Snackbar Alert
|--------------------------------------------------------------------------
*/

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function Basic() {
  /*
  |--------------------------------------------------------------------------
  | State
  |--------------------------------------------------------------------------
  */

  const [rememberMe, setRememberMe] = useState(false);

  const [user_email, setEmail] = useState("");
  const [user_password, setPassword] = useState("");

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const enableNormalLogin = process.env.REACT_APP_ENABLE_NORMAL_LOGIN === "true";

  /*
  |--------------------------------------------------------------------------
  | Remember Me
  |--------------------------------------------------------------------------
  */

  const handleSetRememberMe = () => {
    setRememberMe((previous) => !previous);
  };

  /*
  |--------------------------------------------------------------------------
  | Snackbar
  |--------------------------------------------------------------------------
  */

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  /*
  |--------------------------------------------------------------------------
  | Save Login Session
  |--------------------------------------------------------------------------
  */

  const saveLoginSession = (user, token) => {
    /*
     * Save JWT
     */
    sessionStorage.setItem("token", token);

    /*
     * Save user
     */
    sessionStorage.setItem("user", JSON.stringify(user));

    /*
     * Save selected college
     *
     * The login API should return the user's
     * default/current college.
     */
    if (user?.college_id) {
      sessionStorage.setItem("selected_college_id", String(user.college_id));
    } else {
      sessionStorage.removeItem("selected_college_id");
    }

    /*
     * Save selected role
     *
     * The login API should return the user's
     * default/current role.
     */
    if (user?.role_id) {
      sessionStorage.setItem("selected_role_id", String(user.role_id));
    } else {
      sessionStorage.removeItem("selected_role_id");
    }

    /*
     * Remove old cached RBAC information.
     *
     * It will be loaded again according to:
     *
     * college_id + role_id
     */
    sessionStorage.removeItem("modulefunctions");

    sessionStorage.removeItem("rolepermissions");
  };

  /*
  |--------------------------------------------------------------------------
  | Load Modules For Selected College + Role
  |--------------------------------------------------------------------------
  */

  const loadRoleModules = async (collegeId, roleId) => {
    if (!collegeId || !roleId) {
      return [];
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/rbac/college/${collegeId}/role/${roleId}/modules`
      );

      const modules = response.data?.modules || [];

      if (Array.isArray(modules)) {
        sessionStorage.setItem("modulefunctions", JSON.stringify(modules));
      }

      return modules;
    } catch (error) {
      console.error("Failed to load role modules:", error);

      sessionStorage.removeItem("modulefunctions");

      return [];
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Load Permissions
  |--------------------------------------------------------------------------
  |
  | Permissions are still user based here.
  |
  | If later you want permissions to be strictly
  | college + role based, we can change that API
  | separately.
  |--------------------------------------------------------------------------
  */

  const loadPermissions = async (userId) => {
    if (!userId) {
      return [];
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/rbac/user/${userId}/permissions`);

      const permissions = response.data?.permissions || [];

      if (Array.isArray(permissions)) {
        sessionStorage.setItem("rolepermissions", JSON.stringify(permissions));
      }

      return permissions;
    } catch (error) {
      console.error("Failed to load permissions:", error);

      sessionStorage.removeItem("rolepermissions");

      return [];
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Load RBAC Information
  |--------------------------------------------------------------------------
  */

  const loadRBAC = async (user) => {
    if (!user?.user_id) {
      return;
    }

    /*
     * Load permissions
     */
    await loadPermissions(user.user_id);

    /*
     * Load modules only when we have:
     *
     * college_id
     * role_id
     */
    if (user.college_id && user.role_id) {
      await loadRoleModules(user.college_id, user.role_id);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Check Existing Authentication
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      return;
    }

    axios
      .get(`${API_BASE_URL}/api/auth/check-auth`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(async (res) => {
        if (res.data.success) {
          /*
           * Existing session is valid.
           *
           * Load current user from session.
           */
          const storedUser = sessionStorage.getItem("user");

          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);

              await loadRBAC(user);
            } catch (error) {
              console.error("Invalid stored user:", error);
            }
          }

          navigate("/dashboard", {
            replace: true,
          });
        } else {
          sessionStorage.clear();
        }
      })
      .catch(() => {
        sessionStorage.clear();
      });
  }, [navigate, API_BASE_URL]);

  /*
  |--------------------------------------------------------------------------
  | Normal Login
  |--------------------------------------------------------------------------
  */

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!user_email.trim()) {
      showSnackbar("Please enter your email.", "warning");

      return;
    }

    if (!user_password) {
      showSnackbar("Please enter your password.", "warning");

      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
        email: user_email.trim(),
        password: user_password,
      });

      if (!response.data?.success) {
        showSnackbar(response.data?.message || "Invalid credentials.", "error");

        return;
      }

      const user = response.data.user;

      const token = response.data.token;

      /*
       * IMPORTANT
       *
       * Login API must return:
       *
       * user_id
       * user_name
       * user_email
       * client_id
       * college_id
       * role_id
       */
      saveLoginSession(user, token);

      /*
       * Load permissions/modules
       */
      await loadRBAC(user);

      showSnackbar("Login successful!", "success");

      /*
       * Go to dashboard
       */
      navigate("/dashboard", {
        replace: true,
      });
    } catch (error) {
      console.error("Normal login error:", error);

      showSnackbar(error?.response?.data?.message || "Normal login failed!", "error");
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Google Login
  |--------------------------------------------------------------------------
  */

  const handleGoogleLogin = async (response) => {
    const googleToken = response?.credential;

    if (!googleToken) {
      showSnackbar("Google login failed!", "error");

      return;
    }

    try {
      const backendResponse = await axios.post(`${API_BASE_URL}/api/auth/google-login`, {
        credential: googleToken,
      });

      if (!backendResponse.data?.token || !backendResponse.data?.user) {
        showSnackbar("Google login failed!", "error");

        return;
      }

      const user = backendResponse.data.user;

      const token = backendResponse.data.token;

      /*
       * Save login session
       */
      saveLoginSession(user, token);

      /*
       * Load RBAC
       */
      await loadRBAC(user);

      showSnackbar("Google login successful!", "success");

      navigate("/dashboard", {
        replace: true,
      });
    } catch (error) {
      console.error("Google login error:", error);

      showSnackbar(error?.response?.data?.message || "Google login failed!", "error");
    }
  };

  /*
  |--------------------------------------------------------------------------
  | JSX
  |--------------------------------------------------------------------------
  */

  return (
    <BasicLayout image={bgImage}>
      <Card>
        {/* -------------------------------------------------------------- */}
        {/* Header */}
        {/* -------------------------------------------------------------- */}

        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign In
          </MDTypography>
        </MDBox>

        {/* -------------------------------------------------------------- */}
        {/* Google Login */}
        {/* -------------------------------------------------------------- */}

        <MDBox mt={3} display="flex" flexDirection="column" alignItems="center" px={3}>
          <Card
            sx={{
              p: 2,
              mb: 4,
              borderRadius: "12px",

              background: "linear-gradient(135deg, #9abaeaff 0%, #a7c3e0ff 100%)",

              boxShadow: "0 3px 10px rgba(0, 0, 0, 0.1)",

              transition: "all 0.3s ease",

              "&:hover": {
                transform: "scale(1.03)",
              },
            }}
          >
            <div
              style={{
                transform: "scale(1.0)",
                transformOrigin: "center",
              }}
            >
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => showSnackbar("Google login failed!", "error")}
                width="250"
              />
            </div>
          </Card>

          {enableNormalLogin && (
            <MDTypography
              variant="button"
              color="text"
              sx={{
                mt: 2,
              }}
            >
              OR
            </MDTypography>
          )}
        </MDBox>

        {/* -------------------------------------------------------------- */}
        {/* Normal Login */}
        {/* -------------------------------------------------------------- */}

        {enableNormalLogin && (
          <MDBox pt={3} pb={3} px={3}>
            <MDBox component="form" role="form" onSubmit={handleLogin}>
              {/* Email */}

              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="Email"
                  fullWidth
                  value={user_email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </MDBox>

              {/* Password */}

              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label="Password"
                  fullWidth
                  value={user_password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </MDBox>

              {/* Remember Me */}

              <MDBox display="flex" alignItems="center" ml={-1}>
                <Switch checked={rememberMe} onChange={handleSetRememberMe} />

                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  onClick={handleSetRememberMe}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    ml: -1,
                  }}
                >
                  &nbsp;&nbsp;Remember me
                </MDTypography>
              </MDBox>

              {/* Sign In */}

              <MDBox mt={4} mb={1}>
                <MDButton variant="gradient" color="info" fullWidth type="submit">
                  Sign In
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        )}
      </Card>

      {/* -------------------------------------------------------------- */}
      {/* Snackbar */}
      {/* -------------------------------------------------------------- */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            backgroundColor: snackbarSeverity === "success" ? "#1976d2" : undefined,

            color: "#fff",

            "& .MuiAlert-icon": {
              color: "#fff",
            },

            "& .MuiAlert-action": {
              color: "#fff",
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
}

export default Basic;
